import React, { useEffect, useState, useRef } from 'react';
import { apiService } from '../services/api';

const AutoLessonPlanGenerator = ({ attachedContext, setAttachedContext, onStart, enrichAttachedContext, showGenerationView, setShowGenerationView, generationProgress, generationReasoning, generationContent, clearGenerationView, planId }) => {
  const [autoStep, setAutoStep] = useState(1);
  const [clientName, setClientName] = useState('');
  const [transcript, setTranscript] = useState('');
  const [userMessage, setUserMessage] = useState('');
  const [excelGenerationStatus, setExcelGenerationStatus] = useState('idle'); // 'idle', 'generating', 'completed', 'error'
  const [excelSlides, setExcelSlides] = useState([]);
  const [excelError, setExcelError] = useState('');
  
  // Workshop context fields
  const [workshopFormat, setWorkshopFormat] = useState(''); // 'online', 'offline', 'hybrid'
  const [cohortSize, setCohortSize] = useState('');
  const [averageAge, setAverageAge] = useState('');
  const [genderRatio, setGenderRatio] = useState('');
  
  const persistTimer = useRef(null);
  const STORAGE_KEY = 'autoLP';

  // API base URL for backend calls
  const apiBaseRest = (process.env.REACT_APP_BACKEND_URL || 'https://propulseapi.brainayan.com').replace(/\/$/, '');

  // Load persisted state on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (data && typeof data === 'object') {
        if (typeof data.autoStep === 'number') setAutoStep(data.autoStep);
        if (typeof data.clientName === 'string') setClientName(data.clientName);
        if (typeof data.transcript === 'string') setTranscript(data.transcript);
        if (typeof data.userMessage === 'string') setUserMessage(data.userMessage);
        // Workshop context fields
        if (typeof data.workshopFormat === 'string') setWorkshopFormat(data.workshopFormat);
        if (typeof data.cohortSize === 'string') setCohortSize(data.cohortSize);
        if (typeof data.averageAge === 'string') setAverageAge(data.averageAge);
        if (typeof data.genderRatio === 'string') setGenderRatio(data.genderRatio);
        if (data.attachedContext && typeof data.attachedContext === 'object') {
          setAttachedContext(prev => ({
            ...prev,
            ...data.attachedContext,
          }));
        }
      }
    } catch (_) { }

    // Check for existing Excel slides (only if Excel features are enabled)
    if (planId) {
      fetchExcelSlides(planId);
    }
    // eslint-disable-next-line
  }, []);

  // Persist on changes (debounced)
  useEffect(() => {
    try { if (persistTimer.current) clearTimeout(persistTimer.current); } catch (_) { }
    persistTimer.current = setTimeout(() => {
      try {
        const payload = {
          autoStep,
          clientName,
          transcript,
          userMessage,
          workshopFormat,
          cohortSize,
          averageAge,
          genderRatio,
          attachedContext,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch (_) { }
    }, 300);
    return () => { try { if (persistTimer.current) clearTimeout(persistTimer.current); } catch (_) { } };
  }, [autoStep, clientName, transcript, userMessage, workshopFormat, cohortSize, averageAge, genderRatio, attachedContext]);

  const resetPersisted = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch (_) { }
    setClientName('');
    setTranscript('');
    setUserMessage('');
    setWorkshopFormat('');
    setCohortSize('');
    setAverageAge('');
    setGenderRatio('');
    setAttachedContext({ proposals: [], games: [], frameworks: [], tools: [], psychometricTools: [], transcripts: [] });
    setAutoStep(1);
  };

  // Inline selectors state
  const [search, setSearch] = useState('');
  const [listCategory, setListCategory] = useState('proposals');
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [detailItem, setDetailItem] = useState(null);

  const categoryKey = (cat) => (cat === 'psychometrics' ? 'psychometricTools' : cat);

  const loadList = async (cat, pageNum = 1) => {
    setLoading(true);
    try {
      if (['games', 'frameworks', 'tools', 'psychometrics'].includes(cat)) {
        const response = await apiService.getCatalogItems(cat === 'psychometrics' ? 'psychometrics' : cat, pageNum, 50);
        const q = (search || '').trim().toLowerCase();
        const filtered = (response.items || []).filter(i => !q || (i.name || '').toLowerCase().includes(q));
        const scored = filtered.map(i => {
          const nameLc = (i.name || '').toLowerCase();
          const priority = !q ? 2 : (nameLc.startsWith(q) ? 0 : (nameLc.includes(q) ? 1 : 2));
          return { item: i, priority, nameLc };
        });
        scored.sort((a, b) => a.priority - b.priority || a.nameLc.localeCompare(b.nameLc));
        const items = scored.map(s => s.item);
        setList(pageNum === 1 ? items : [...list, ...items]);
        setTotal(response.total || items.length);
      } else if (cat === 'proposals') {
        // Fetch reference proposals using the same API as Proposal Generator
        try {
          const res = await apiService.getReferenceProposals();
          const all = (res && (res.proposals || res)) || [];
          const normalized = all.map(p => ({ id: p.id || p._id || p.uuid || p.title, name: p.title || p.name || 'Proposal', date: p.date, content: p.content }));
          const q = (search || '').trim().toLowerCase();
          const filtered = normalized.filter(i => !q || (i.name || '').toLowerCase().includes(q));
          const scored = filtered.map(i => {
            const nameLc = (i.name || '').toLowerCase();
            const priority = !q ? 2 : (nameLc.startsWith(q) ? 0 : (nameLc.includes(q) ? 1 : 2));
            return { item: i, priority, nameLc };
          });
          scored.sort((a, b) => a.priority - b.priority || a.nameLc.localeCompare(b.nameLc));
          const sorted = scored.map(s => s.item);
          const pageSize = 50;
          const start = (pageNum - 1) * pageSize;
          const paged = sorted.slice(start, start + pageSize);
          setList(paged);
          setTotal(sorted.length);
        } catch (e) {
          console.error('Failed to load reference proposals:', e);
          setList([]);
          setTotal(0);
        }
      } else if (cat === 'transcripts') {
        setList([]);
        setTotal(0);
      }
      setPage(pageNum);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Load default list on step open
    if (autoStep === 2) {
      setListCategory('proposals');
      loadList('proposals', 1);
    }
    if (autoStep === 3) {
      setListCategory('games');
      loadList('games', 1);
    }
    // Log resources when on review step (step 5)
    if (autoStep === 5) {
      console.log('LP_DEBUG: Step 5 (Review) - Current attachedContext state:', {
        proposals_count: (attachedContext.proposals || []).length,
        games_count: (attachedContext.games || []).length,
        frameworks_count: (attachedContext.frameworks || []).length,
        tools_count: (attachedContext.tools || []).length,
        psychometrics_count: (attachedContext.psychometricTools || []).length,
        transcripts_count: (attachedContext.transcripts || []).length,
        first_proposal_name: attachedContext.proposals?.[0]?.name || attachedContext.proposals?.[0]?.title,
        first_proposal_hasContent: !!attachedContext.proposals?.[0]?.content,
        first_proposal_contentLength: attachedContext.proposals?.[0]?.content?.length || 0,
        first_proposal_keys: attachedContext.proposals?.[0] ? Object.keys(attachedContext.proposals[0]) : [],
      });
    }
    // eslint-disable-next-line
  }, [autoStep, attachedContext]);

  const toggleItem = (cat, item) => {
    const key = categoryKey(cat);
    // Debug logging for ALL resource types
    console.log('LP_DEBUG toggleItem:', {
      category: cat,
      itemId: item.id,
      itemName: item.name || item.title,
      hasContent: 'content' in item,
      contentLength: item.content ? item.content.length : 0,
      itemKeys: Object.keys(item),
      isAdding: !((attachedContext[key] || []).find(i => i.id === item.id))
    });
    setAttachedContext(prev => {
      const arr = prev[key] || [];
      const exists = arr.find(i => i.id === item.id);
      const updated = { ...prev, [key]: exists ? arr.filter(i => i.id !== item.id) : [...arr, item] };
      // Debug logging - comprehensive for ALL resource types
      console.log('LP_DEBUG after toggleItem update:', {
        category: cat,
        totalResourcesInCategory: updated[key]?.length || 0,
        allCategories: Object.keys(updated),
        proposals_count: updated.proposals?.length || 0,
        games_count: updated.games?.length || 0,
        frameworks_count: updated.frameworks?.length || 0,
        tools_count: updated.tools?.length || 0,
        psychometrics_count: updated.psychometricTools?.length || 0,
        transcripts_count: updated.transcripts?.length || 0,
        firstProposal_name: updated.proposals?.[0]?.name || updated.proposals?.[0]?.title,
        firstProposal_hasContent: !!updated.proposals?.[0]?.content,
        firstProposal_contentLength: updated.proposals?.[0]?.content?.length || 0,
      });
      return updated;
    });
  };

  const renderInlineSelector = (cat) => {
    const key = categoryKey(cat);
    const pageSize = 50;
    const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
    const goTo = (n) => { if (n >= 1 && n <= totalPages) loadList(cat, n); };
    const pages = (() => {
      const arr = [];
      const windowSize = 5; // show current ±2
      const start = Math.max(1, page - 2);
      const end = Math.min(totalPages, start + windowSize - 1);
      for (let i = start; i <= end; i++) arr.push(i);
      return arr;
    })();
    return (
      <div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${cat}`} style={{ flex: 1, borderRadius: 8, border: '1px solid #334155', background: '#0b1324', color: '#e2e8f0', padding: '8px 10px', fontSize: 12 }} />
          <button onClick={() => loadList(cat, 1)} style={{ borderRadius: 6, border: '1px solid #334155', background: 'rgba(59,130,246,0.25)', color: '#bfdbfe', padding: '8px 12px', fontSize: 12 }}>Search</button>
        </div>
        <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #334155', borderRadius: 8, position: 'relative' }}>
          {loading ? (
            <div style={{ padding: 12, color: '#94a3b8' }}>Loading…</div>
          ) : (
            list.map(item => {
              const selected = (attachedContext[key] || []).some(i => i.id === item.id);
              const desc = item.detailed_explanation || item.description || item.summary || item.what || '';
              return (
                <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', borderBottom: '1px solid #1f2937', color: '#cbd5e1' }}>
                  <input type="checkbox" checked={!!selected} onChange={() => toggleItem(cat, item)} style={{ marginTop: 3 }} />
                  <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setDetailItem(item)}>
                    <div style={{ fontWeight: 600 }}>{item.name || 'Item'}</div>
                    {desc && <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>{String(desc).slice(0, 140)}{String(desc).length > 140 ? '…' : ''}</div>}
                    <div style={{ display: 'flex', gap: 10, marginTop: 4, fontSize: 11, color: '#64748b' }}>
                      {item.provider && <span>Provider: {item.provider}</span>}
                      {item.platform && <span>Platform: {item.platform}</span>}
                      {item.industry && <span>Industry: {item.industry}</span>}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        {!loading && totalPages > 1 && (
          <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center', marginTop: 8 }}>
            <button onClick={() => goTo(page - 1)} disabled={page === 1} style={{ borderRadius: 6, border: '1px solid #334155', background: page === 1 ? 'rgba(51,65,85,0.5)' : 'rgba(59,130,246,0.2)', color: '#bfdbfe', padding: '6px 10px', fontSize: 12, cursor: page === 1 ? 'not-allowed' : 'pointer' }}>Prev</button>
            {pages.map(p => (
              <button key={p} onClick={() => goTo(p)} style={{ borderRadius: 6, border: '1px solid #334155', background: p === page ? 'rgba(59,130,246,0.35)' : 'transparent', color: '#e2e8f0', padding: '6px 10px', fontSize: 12, cursor: 'pointer' }}>{p}</button>
            ))}
            <button onClick={() => goTo(page + 1)} disabled={page === totalPages} style={{ borderRadius: 6, border: '1px solid #334155', background: page === totalPages ? 'rgba(51,65,85,0.5)' : 'rgba(59,130,246,0.2)', color: '#bfdbfe', padding: '6px 10px', fontSize: 12, cursor: page === totalPages ? 'not-allowed' : 'pointer' }}>Next</button>
          </div>
        )}
        <div style={{ marginTop: 8, color: '#94a3b8', fontSize: 12 }}>Selected: {(attachedContext[key] || []).length}</div>
      </div>
    );
  };

  const start = async () => {
    // Debug: Print the full attachedContext at generation time
    console.log('LP_DEBUG: [GENERATION] attachedContext at start:', JSON.stringify(attachedContext, null, 2));

    const enriched = enrichAttachedContext(attachedContext);
    // Debug: Print the enriched context
    console.log('LP_DEBUG: [GENERATION] enriched context:', JSON.stringify(enriched, null, 2));

    // Prepare payload in current structure, using the latest attachedContext (not just enriched)
    const payload = {
      clientName,
      transcript,
      userMessage,
      proposals: attachedContext.proposals || [],
      games: attachedContext.games || [],
      frameworks: attachedContext.frameworks || [],
      tools: attachedContext.tools || [],
      psychometricTools: attachedContext.psychometricTools || [],
      transcripts: attachedContext.transcripts || [],
      // Workshop context fields
      workshopContext: {
        format: workshopFormat || null,
        cohortSize: cohortSize ? parseInt(cohortSize, 10) : null,
        averageAge: averageAge ? parseInt(averageAge, 10) : null,
        genderRatio: genderRatio || null,
      },
    };

    // Debug: Print the payload being sent
    console.log('LP_DEBUG: [GENERATION] payload sent to backend:', JSON.stringify(payload, null, 2));

    try {
      await onStart(userMessage, payload, { clientName, transcript, workshopContext: payload.workshopContext });
    } catch (err) {
      console.error('Lesson plan generation failed:', err);
    }
  };

  // Excel generation functions
  const generateExcelSlides = async () => {
    if (excelGenerationStatus === 'generating') return;

    setExcelGenerationStatus('generating');
    setExcelError('');

    try {
      // Use plan ID passed from parent component
      if (!planId) {
        setExcelGenerationStatus('error');
        setExcelError('No lesson plan ID available');
        return;
      }

      const response = await fetch(`${apiBaseRest}/api/lp/plan/${planId}/generate-excel/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success) {
        setExcelGenerationStatus('completed');
        // Fetch the generated slides
        await fetchExcelSlides(planId);
      } else {
        setExcelGenerationStatus('error');
        setExcelError(result.error || 'Failed to generate Excel slides');
      }
    } catch (error) {
      setExcelGenerationStatus('error');
      setExcelError(error.message || 'Network error occurred');
    }
  };

  const fetchExcelSlides = async (planId) => {
    try {
      const response = await fetch(`${apiBaseRest}/api/lp/plan/${planId}/excel-slides/`);
      
      // Handle 404 gracefully (plan doesn't exist yet)
      if (response.status === 404) {
        setExcelSlides([]);
        return;
      }
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();

      if (result.success && result.slides.length > 0) {
        setExcelSlides(result.slides);
        setExcelGenerationStatus('completed');
      } else {
        setExcelSlides([]);
      }
    } catch (error) {
      // Only log non-404 errors
      if (!error.message.includes('404')) {
        console.error('Error fetching Excel slides:', error);
      }
      setExcelSlides([]);
    }
  };

  const downloadExcelFile = async () => {
    if (excelSlides.length === 0) return;

    try {
      // Use plan ID passed from parent component
      if (!planId) {
        setExcelError('No lesson plan ID available');
        return;
      }

      // Download Excel file from backend
      const response = await fetch(`${apiBaseRest}/api/lp/plan/${planId}/excel-slides/download/`);

      if (response.ok) {
        // Get filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `${clientName || 'LessonPlan'}_Slides.xlsx`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        // Create blob and download
        const blob = await response.blob();
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        setExcelError('Failed to download Excel file');
      }
    } catch (error) {
      setExcelError('Error downloading Excel file');
    }
  };

  return (
    <div style={{ border: '1px solid #334155', borderRadius: 12, background: 'rgba(2,6,23,0.6)', marginBottom: 16, display: 'flex', minHeight: 240 }}>
      <div style={{ width: 240, borderRight: '1px solid #334155', padding: 14 }}>
        {[
          { id: 1, label: 'Client & Transcript' },
          { id: 2, label: 'Template & References' },
          { id: 3, label: 'Training Resources' },
          { id: 4, label: 'User Message' },
          { id: 5, label: 'Generation' },
          { id: 6, label: 'Generation View' }
        ].map(step => (
          <button key={step.id}
            onClick={() => setAutoStep(step.id)}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              padding: '10px 12px', marginBottom: 6,
              borderRadius: 8, border: '1px solid #334155',
              background: autoStep === step.id ? 'rgba(59,130,246,0.22)' : 'transparent',
              color: autoStep === step.id ? '#bfdbfe' : '#94a3b8',
              fontSize: 13, cursor: 'pointer'
            }}>{step.label}</button>
        ))}
      </div>

      <div style={{ flex: 1, padding: 16 }}>
        {autoStep === 1 && (
          <>
            <div style={{ marginBottom: 10, color: '#94a3b8', fontSize: 12 }}>Provide required details: requirements and context</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <div style={{ color: '#e2e8f0', fontSize: 12, marginBottom: 6 }}>Client / Program Name</div>
                <input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Enter name" style={{ width: '100%', borderRadius: 8, border: '1px solid #334155', background: '#0b1324', color: '#e2e8f0', padding: '10px 12px', fontSize: 13 }} />
              </div>
              <div>
                <div style={{ color: '#e2e8f0', fontSize: 12, marginBottom: 6 }}>Requirements & Context</div>
                <textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} placeholder="Enter requirements and context: notes, chat transcript, written info, constraints, links, etc." style={{ width: '100%', minHeight: 120, borderRadius: 8, padding: 10, background: '#0b1324', color: '#e2e8f0', border: '1px solid #334155', fontFamily: 'monospace', fontSize: 12 }} />
              </div>
              
              {/* Workshop Context Fields */}
              <div style={{ borderTop: '1px solid #334155', paddingTop: 12, marginTop: 4 }}>
                <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Workshop Context (Optional)</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <div style={{ color: '#e2e8f0', fontSize: 12, marginBottom: 6 }}>Workshop Format</div>
                    <select 
                      value={workshopFormat} 
                      onChange={(e) => setWorkshopFormat(e.target.value)} 
                      style={{ width: '100%', borderRadius: 8, border: '1px solid #334155', background: '#0b1324', color: '#e2e8f0', padding: '10px 12px', fontSize: 13 }}
                    >
                      <option value="">Select format...</option>
                      <option value="online">Online (Virtual)</option>
                      <option value="offline">Offline (In-Person)</option>
                      <option value="hybrid">Hybrid</option>
                    </select>
                  </div>
                  <div>
                    <div style={{ color: '#e2e8f0', fontSize: 12, marginBottom: 6 }}>Cohort Size</div>
                    <input 
                      type="number" 
                      value={cohortSize} 
                      onChange={(e) => setCohortSize(e.target.value)} 
                      placeholder="e.g., 25" 
                      min="1"
                      style={{ width: '100%', borderRadius: 8, border: '1px solid #334155', background: '#0b1324', color: '#e2e8f0', padding: '10px 12px', fontSize: 13 }} 
                    />
                  </div>
                  <div>
                    <div style={{ color: '#e2e8f0', fontSize: 12, marginBottom: 6 }}>Average Age</div>
                    <input 
                      type="number" 
                      value={averageAge} 
                      onChange={(e) => setAverageAge(e.target.value)} 
                      placeholder="e.g., 32" 
                      min="18"
                      max="80"
                      style={{ width: '100%', borderRadius: 8, border: '1px solid #334155', background: '#0b1324', color: '#e2e8f0', padding: '10px 12px', fontSize: 13 }} 
                    />
                  </div>
                  <div>
                    <div style={{ color: '#e2e8f0', fontSize: 12, marginBottom: 6 }}>Gender Ratio</div>
                    <input 
                      value={genderRatio} 
                      onChange={(e) => setGenderRatio(e.target.value)} 
                      placeholder="e.g., 60% male, 40% female" 
                      style={{ width: '100%', borderRadius: 8, border: '1px solid #334155', background: '#0b1324', color: '#e2e8f0', padding: '10px 12px', fontSize: 13 }} 
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {autoStep === 2 && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ color: '#94a3b8', fontSize: 12 }}>Select past proposals as references.</div>
              <button onClick={() => setAttachedContext(prev => ({ ...prev, proposals: [] }))} style={{ borderRadius: 6, border: '1px solid #334155', background: 'rgba(239,68,68,0.2)', color: '#fca5a5', padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}>Unselect Proposals</button>
            </div>
            {renderInlineSelector('proposals')}
          </>
        )}

        {autoStep === 3 && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ color: '#94a3b8', fontSize: 12 }}>Select training resources.</div>
              <button onClick={() => setAttachedContext(prev => ({ ...prev, games: [], frameworks: [], tools: [], psychometricTools: [] }))} style={{ borderRadius: 6, border: '1px solid #334155', background: 'rgba(239,68,68,0.2)', color: '#fca5a5', padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}>Clear Resources</button>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              {['games', 'frameworks', 'tools', 'psychometrics'].map(cat => (
                <button key={cat} onClick={() => { setListCategory(cat); loadList(cat, 1); }} style={{ borderRadius: 6, border: '1px solid #334155', background: listCategory === cat ? 'rgba(34,197,94,0.25)' : 'transparent', color: '#e2e8f0', padding: '6px 10px', fontSize: 12, cursor: 'pointer' }}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</button>
              ))}
            </div>
            {renderInlineSelector(listCategory)}
          </>
        )}

        {autoStep === 4 && (
          <>
            <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>Your message to the LLM</div>
            <textarea value={userMessage} onChange={(e) => setUserMessage(e.target.value)} placeholder="Objectives, structure, constraints, tone, competencies…" style={{ width: '100%', minHeight: 120, borderRadius: 8, padding: 10, background: '#0b1324', color: '#e2e8f0', border: '1px solid #334155', fontFamily: 'monospace', fontSize: 12 }} />
          </>
        )}

        {autoStep === 5 && (
          <>
            <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>Review summary and start generation.</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12, color: '#cbd5e1', fontSize: 12 }}>
              <div>Client/Program: <span style={{ color: '#e2e8f0' }}>{clientName || '—'}</span></div>
              <div>Proposals: {(attachedContext.proposals || []).length}</div>
              <div>Games: {(attachedContext.games || []).length}</div>
              <div>Frameworks: {(attachedContext.frameworks || []).length}</div>
              <div>Tools: {(attachedContext.tools || []).length}</div>
              <div>Psychometrics: {(attachedContext.psychometricTools || []).length}</div>
              <div>Transcripts: {(attachedContext.transcripts || []).length}</div>
            </div>
            {/* Workshop Context Summary */}
            {(workshopFormat || cohortSize || averageAge || genderRatio) && (
              <div style={{ borderTop: '1px solid #334155', paddingTop: 10, marginBottom: 12 }}>
                <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Workshop Context</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, color: '#cbd5e1', fontSize: 12 }}>
                  {workshopFormat && <div>Format: <span style={{ color: '#e2e8f0' }}>{workshopFormat === 'online' ? 'Online (Virtual)' : workshopFormat === 'offline' ? 'Offline (In-Person)' : 'Hybrid'}</span></div>}
                  {cohortSize && <div>Cohort Size: <span style={{ color: '#e2e8f0' }}>{cohortSize} participants</span></div>}
                  {averageAge && <div>Average Age: <span style={{ color: '#e2e8f0' }}>{averageAge} years</span></div>}
                  {genderRatio && <div>Gender Ratio: <span style={{ color: '#e2e8f0' }}>{genderRatio}</span></div>}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={start} style={{ borderRadius: 8, border: '1px solid rgba(59,130,246,0.5)', background: 'rgba(59,130,246,0.25)', color: '#bfdbfe', padding: '10px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Start Generation</button>
              <button onClick={resetPersisted} title="Clear saved inputs" style={{ borderRadius: 8, border: '1px solid #334155', background: 'rgba(51,65,85,0.6)', color: '#94a3b8', padding: '10px 12px', fontSize: 12, cursor: 'pointer' }}>Reset</button>
            </div>
          </>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
          <button onClick={() => setAutoStep(Math.max(1, autoStep - 1))} disabled={autoStep === 1} style={{ borderRadius: 6, border: '1px solid #334155', background: autoStep === 1 ? 'rgba(51,65,85,0.6)' : 'rgba(51,65,85,0.3)', color: '#94a3b8', padding: '8px 12px', fontSize: 12, cursor: autoStep === 1 ? 'not-allowed' : 'pointer' }}>Back</button>
          <button onClick={() => setAutoStep(Math.min(5, autoStep + 1))} disabled={autoStep === 5} style={{ borderRadius: 6, border: '1px solid rgba(59,130,246,0.5)', background: autoStep === 5 ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.25)', color: '#bfdbfe', padding: '8px 12px', fontSize: 12, cursor: autoStep === 5 ? 'not-allowed' : 'pointer' }}>Next</button>
        </div>

        {/* Generation View as a normal step */}
        {autoStep === 6 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0', margin: 0 }}>Generation View</h3>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {/* Excel Generation Button */}
                {planId && (
                  <button
                    onClick={generateExcelSlides}
                    disabled={excelGenerationStatus === 'generating'}
                    style={{
                      borderRadius: 6,
                      border: '1px solid #059669',
                      background: excelGenerationStatus === 'generating' ? 'rgba(5,150,105,0.3)' : 'rgba(5,150,105,0.6)',
                      color: '#d1fae5',
                      padding: '4px 8px',
                      fontSize: 11,
                      cursor: excelGenerationStatus === 'generating' ? 'not-allowed' : 'pointer',
                      opacity: excelGenerationStatus === 'generating' ? 0.7 : 1
                    }}
                  >
                    {excelGenerationStatus === 'generating' ? 'Generating...' : 'Generate Excel'}
                  </button>
                )}
                <button onClick={clearGenerationView} style={{ borderRadius: 6, border: '1px solid #334155', background: 'rgba(51,65,85,0.6)', color: '#94a3b8', padding: '4px 8px', fontSize: 11, cursor: 'pointer' }}>Clear</button>
              </div>
            </div>

            {/* Excel Generation Status */}
            {excelGenerationStatus !== 'idle' && (
              <div style={{ marginBottom: 8, padding: 8, borderRadius: 6, fontSize: 12 }}>
                {excelGenerationStatus === 'generating' && (
                  <div style={{ color: '#fbbf24', background: 'rgba(251,191,36,0.1)', padding: '6px 8px', borderRadius: 4 }}>
                    ⏳ Generating Excel slides... This may take a few minutes.
                  </div>
                )}
                {excelGenerationStatus === 'completed' && (
                  <div style={{ color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '6px 8px', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>✅ Excel slides generated successfully! ({excelSlides.length} slides)</span>
                    <button
                      onClick={downloadExcelFile}
                      style={{
                        background: 'rgba(16,185,129,0.3)',
                        border: '1px solid #10b981',
                        color: '#d1fae5',
                        padding: '2px 6px',
                        borderRadius: 4,
                        fontSize: 10,
                        cursor: 'pointer'
                      }}
                    >
                      Download Excel
                    </button>
                  </div>
                )}
                {excelGenerationStatus === 'error' && (
                  <div style={{ color: '#ef4444', background: 'rgba(239,68,68,0.1)', padding: '6px 8px', borderRadius: 4 }}>
                    ❌ Error: {excelError}
                  </div>
                )}
              </div>
            )}
            <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>
              {generationProgress.stage ? (
                <span>
                  Stage: {generationProgress.stage} {generationProgress.index ? `(${generationProgress.index}/${generationProgress.total})` : ''}
                  {generationProgress.sectionTitle ? ` — ${generationProgress.sectionTitle}` : ''}
                </span>
              ) : 'Idle'}
            </div>
            {!!generationReasoning && (
              <div style={{ marginBottom: 10, padding: 10, border: '1px dashed #334155', borderRadius: 8, background: 'rgba(2,6,23,0.5)', color: '#a5b4fc', fontSize: 12, whiteSpace: 'pre-wrap' }}>
                {generationReasoning}
              </div>
            )}
            <div style={{ border: '1px solid #334155', borderRadius: 8, padding: 10, background: 'rgba(2,6,23,0.6)', color: '#cbd5e1', fontSize: 12, whiteSpace: 'pre-wrap', minHeight: 200, maxHeight: 400, overflowY: 'auto' }}>
              {generationContent || '(waiting for content…)'}
            </div>
          </div>
        )}
      </div>
      {/* Slide-in detail view */}
      {detailItem && (
        <div style={{ position: 'fixed', top: 64, right: 0, bottom: 0, width: '40%', minWidth: 340, background: 'rgba(15,23,42,0.98)', borderLeft: '1px solid #334155', zIndex: 100000, boxShadow: '-6px 0 20px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', transform: 'translateX(0)', transition: 'transform 0.3s ease' }}>
          {/* Floating close */}
          <button onClick={() => setDetailItem(null)} title="Close" style={{ position: 'absolute', top: 10, right: 10, width: 28, height: 28, borderRadius: 6, border: '1px solid #334155', background: 'rgba(51,65,85,0.6)', color: '#e2e8f0', cursor: 'pointer' }}>✕</button>
          <div style={{ padding: '18px 20px 8px 20px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 15 }}>{detailItem.name || 'Item'}</div>
          </div>
          <div style={{ padding: 20, overflowY: 'auto', color: '#cbd5e1', fontSize: 13 }}>
            {detailItem.detailed_explanation && (<div style={{ marginBottom: 12 }}><div style={{ color: '#94a3b8', fontSize: 12 }}>Description</div>{detailItem.detailed_explanation}</div>)}
            {detailItem.description && (<div style={{ marginBottom: 12 }}><div style={{ color: '#94a3b8', fontSize: 12 }}>Summary</div>{detailItem.description}</div>)}
            {detailItem.summary && !detailItem.description && (<div style={{ marginBottom: 12 }}><div style={{ color: '#94a3b8', fontSize: 12 }}>Summary</div>{detailItem.summary}</div>)}
            {detailItem.what && (<div style={{ marginBottom: 12 }}><div style={{ color: '#94a3b8', fontSize: 12 }}>What</div>{detailItem.what}</div>)}
            {detailItem.why && (<div style={{ marginBottom: 12 }}><div style={{ color: '#94a3b8', fontSize: 12 }}>Why</div>{detailItem.why}</div>)}
            {detailItem.platform && (<div style={{ marginBottom: 12 }}><div style={{ color: '#94a3b8', fontSize: 12 }}>Platform</div>{detailItem.platform}</div>)}
            {detailItem.provider && (<div style={{ marginBottom: 12 }}><div style={{ color: '#94a3b8', fontSize: 12 }}>Provider</div>{detailItem.provider}</div>)}
            {detailItem.industry && (<div style={{ marginBottom: 12 }}><div style={{ color: '#94a3b8', fontSize: 12 }}>Industry</div>{detailItem.industry}</div>)}
            {detailItem.assessment_type && (<div style={{ marginBottom: 12 }}><div style={{ color: '#94a3b8', fontSize: 12 }}>Type</div>{detailItem.assessment_type}</div>)}
            {detailItem.date && (<div style={{ marginBottom: 12 }}><div style={{ color: '#94a3b8', fontSize: 12 }}>Date</div>{detailItem.date}</div>)}

            {/* Render any additional string/number fields for transparency */}
            {(() => {
              const known = new Set(['id', 'name', 'detailed_explanation', 'description', 'summary', 'what', 'why', 'platform', 'provider', 'industry', 'assessment_type', 'date', 'content']);
              const extras = Object.entries(detailItem || {}).filter(([k, v]) => !known.has(k) && (typeof v === 'string' || typeof v === 'number') && String(v).trim());
              return extras.length ? (
                <div style={{ marginTop: 16 }}>
                  <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 6 }}>Additional Info</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', rowGap: 8 }}>
                    {extras.map(([k, v]) => (
                      <div key={k}>
                        <div style={{ color: '#64748b', fontSize: 11 }}>{k}</div>
                        <div>{String(v)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null;
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoLessonPlanGenerator;


